import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { pb, LiveChatSession, LiveChatMessage } from '@/lib/pocketbase';
import { adminNotificationService } from '@/services/adminNotificationService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { X, MessageCircle, Send, Minimize, Maximize } from 'lucide-react';
import { toast } from 'sonner';

const LiveChatWidget = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [visitorName, setVisitorName] = useState('');
  const [visitorEmail, setVisitorEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [session, setSession] = useState<LiveChatSession | null>(null);
  const [messages, setMessages] = useState<LiveChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [step, setStep] = useState<'form' | 'chat'>('form');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [supportContacts, setSupportContacts] = useState<
    Array<{ id: string; phone_number: string; label: string }>
  >([]);
  const [isLoadingSupportContacts, setIsLoadingSupportContacts] = useState(false);

  const supportContactsFetchedRef = useRef(false);
  const supportContactsFetchInFlightRef = useRef(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchSupportContacts = async () => {
    if (supportContactsFetchedRef.current || supportContactsFetchInFlightRef.current) return;

    supportContactsFetchInFlightRef.current = true;
    setIsLoadingSupportContacts(true);

    try {
      const records = await pb.collection('support_contacts').getFullList({
        filter: 'is_active = true',
        sort: 'display_order,created',
      });

      setSupportContacts(
        records.map((r) => ({
          id: r.id,
          phone_number: r.phone_number,
          label: r.label,
        }))
      );

      supportContactsFetchedRef.current = true;
    } catch (error) {
      console.error('Error fetching support contacts:', error);
      // Allow retry next time the widget opens
      supportContactsFetchedRef.current = false;
    } finally {
      supportContactsFetchInFlightRef.current = false;
      setIsLoadingSupportContacts(false);
    }
  };

  // Prefetch support contacts as soon as the widget mounts (so WhatsApp button is instant on open)
  useEffect(() => {
    fetchSupportContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If prefetch failed, retry when the widget opens
  useEffect(() => {
    if (isOpen) {
      fetchSupportContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Removed auto-hide on scroll - widget stays visible

  useEffect(() => {
    let messageUnsubscribe: (() => void) | null = null;
    let sessionUnsubscribe: (() => void) | null = null;

    const fetchMessages = async () => {
      if (!session?.id) return;

      try {        
        const records = await pb.collection('liveChatMessages').getList(1, 100, {
          filter: `sessionId = "${session.id}"`,
          sort: 'created',
          requestKey: `widget-messages-${session.id}-${Date.now()}`
        });
        setMessages(records.items as LiveChatMessage[]);
        setError(null);
        
        // Check if there are any agent messages to update status
        const hasAgentMessages = records.items.some(msg => 
          msg.senderType === 'agent' && msg.senderType !== 'system'
        );
        
        if (hasAgentMessages && session.status === 'waiting') {
          // Update session status to active if agent has responded
          try {
            const updatedSession = await pb.collection('liveChatSessions').update(session.id, {
              status: 'active',
              assignedAt: new Date().toISOString()
            });
            setSession(updatedSession as LiveChatSession);
          } catch (error) {
            console.error('Error updating session status:', error);
          }
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          setError('Failed to load messages');
        }
      }
    };

    const fetchSessionStatus = async () => {
      if (!session?.id) return;

      try {
        const updatedSession = await pb.collection('liveChatSessions').getOne(session.id, {
          requestKey: `widget-session-${session.id}-${Date.now()}`
        });
        setSession(updatedSession as LiveChatSession);
        setError(null);
        } catch (error: any) {
        if (error.name !== 'AbortError') {
          if (error.status === 404) {
            setError('Chat session no longer exists');
            setSession(null);
            setStep('form');
          } else {
            setError('Failed to check session status');
          }
        }
      }
    };

    const setupSubscriptions = async () => {
      if (session?.id) {
        await fetchMessages();
        await fetchSessionStatus();
        
        try {
          messageUnsubscribe = await pb.collection('liveChatMessages').subscribe('*', (e) => {
            console.log('Widget: Message polling update triggered');
            fetchMessages();
          });

          sessionUnsubscribe = await pb.collection('liveChatSessions').subscribe(session.id, (e) => {
            console.log('Widget: Session polling update triggered');
            fetchSessionStatus();
          });
        } catch (error) {
          console.error('Error setting up subscriptions:', error);
        }
      }
    };

    setupSubscriptions();

    return () => {
      if (messageUnsubscribe) {
        messageUnsubscribe();
      }
      if (sessionUnsubscribe) {
        sessionUnsubscribe();
      }
    };
  }, [session?.id]);

  const startChat = async () => {
    if (!visitorName.trim()) {
      toast.error('Please enter your name');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check for available online agents first
      let sessionStatus = 'waiting';
      let assignedAgentId = undefined;
      
      try {
        const onlineAgents = await pb.collection('liveChatAgents').getList(1, 1, {
          filter: 'status = "online" && isActive = true',
          sort: 'currentChatCount'
        });
        
        if (onlineAgents.items.length > 0) {
          sessionStatus = 'assigned'; // Assigned but not active until agent accepts
          assignedAgentId = onlineAgents.items[0].id;
        }
      } catch (error) {
        console.log('No online agents available');
      }

      const sessionData = {
        visitorName: visitorName.trim(),
        visitorEmail: visitorEmail.trim() || undefined,
        subject: subject.trim() || undefined,
        status: sessionStatus,
        priority: 'normal',
        startedAt: new Date().toISOString(),
        assignedAt: assignedAgentId ? new Date().toISOString() : undefined,
        agentId: assignedAgentId,
        channel: 'live-chat'
      };

      const newSession = await pb.collection('liveChatSessions').create(sessionData);

      setSession(newSession as LiveChatSession);
      setStep('chat');

      const systemMessage = assignedAgentId 
        ? `${visitorName.trim()} has joined the chat. An agent has been assigned and will accept shortly.`
        : `${visitorName.trim()} has joined the chat. Please wait for an agent to respond.`;

      const messageData = {
        sessionId: newSession.id,
        senderName: 'System',
        senderType: 'system',
        message: systemMessage,
        messageType: 'system',
        isRead: false
      };

      await pb.collection('liveChatMessages').create(messageData);

      // Send admin notification for new live chat
      try {
        await adminNotificationService.sendToAllAdmins({
          type: 'live_chat',
          title: 'New Live Chat Started',
          message: `${visitorName.trim()} started a live chat session${subject.trim() ? `: "${subject.trim()}"` : ''}`,
          entityId: newSession.id,
          entityType: 'live_chat_session',
          data: { 
            visitorName: visitorName.trim(), 
            subject: subject.trim(),
            visitorEmail: visitorEmail.trim(),
            sessionId: newSession.id
          }
        });
      } catch (notificationError) {
        // Don't fail the chat creation if notification fails
      }
      
      // Don't show toast for session assignment - keep it internal to the widget
    } catch (error: any) {
      setError('Failed to start chat. Please try again.');
      toast.error('Failed to start chat');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPressOnForm = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      startChat();
    }
  };

  const sendMessage = async () => {
    if (!session?.id || !newMessage.trim() || isLoading) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    setIsLoading(true);

    try {
      const messageData = {
        sessionId: session.id,
        senderName: visitorName.trim(),
        senderType: 'visitor',
        message: messageText,
        messageType: 'text',
        isRead: false
      };

      await pb.collection('liveChatMessages').create(messageData);
      
      // Send notification to all live chat agents about new live chat message
      try {
        console.log('🔄 Starting live chat agent notifications for live chat message...');
        
        // Get Supabase client
        const { supabase } = await import('@/integrations/supabase/client');
        
        console.log('✅ Supabase client imported successfully');
        
        // Get all active live chat agents from PocketBase (pb is already available in component)
        const agents = await pb.collection('liveChatAgents').getList(1, 50, {
          filter: 'isOnline = true',
          expand: 'user'
        });
        
        console.log(`📋 Found ${agents.items.length} active live chat agents`);
        
        if (agents.items.length === 0) {
          console.log('⚠️ No active live chat agents found, sending to generic admin notification');
          
          // Fallback to generic admin notification
          // TODO: Restore when database schema is available
          const error = null; // Temporarily disabled
          /*
          const { error } = await supabase.from('notifications').insert({
            user_id: 'admin_notification',
            type: 'new_message',  
            title: 'New Live Chat Message (No Agents Online)',
            message: `${visitorName.trim()} sent a live chat message: "${messageText.substring(0, 50)}..." - No agents online!`,
            is_read: false,
            data: { 
              visitorName: visitorName.trim(), 
              messageText: messageText.substring(0, 100),
              sessionId: session.id,
              urgency: 'high'
            }
          });
          */
          
          if (error) {
            console.error('❌ Fallback notification error:', error);
          } else {
            console.log('✅ Fallback admin notification sent');
          }
        } else {
          // Send notifications to all active agents
          for (const agent of agents.items) {
            const agentUserId = agent.user;
            
            console.log(`📤 Sending notification to agent: ${agentUserId}`);
            
            try {
              // TODO: Restore when database schema is available
              const error = null; // Temporarily disabled
              /*
              const { error } = await supabase.from('notifications').insert({
                user_id: agentUserId,
                type: 'new_message',
                title: 'New Live Chat Message',
                message: `${visitorName.trim()} sent a live chat message: "${messageText.substring(0, 50)}..."`,
                is_read: false,
                data: { 
                  visitorName: visitorName.trim(), 
                  messageText: messageText.substring(0, 100),
                  sessionId: session.id,
                  agentId: agent.id
                }
              });
              */
              
              if (error) {
                console.error(`❌ Failed to send notification to agent ${agentUserId}:`, error);
              } else {
                console.log(`✅ Notification sent successfully to agent ${agentUserId}`);
              }
              
              // Also send email notification to this agent
              const { supabaseNotificationService } = await import('@/services/supabaseNotificationService');
              await supabaseNotificationService.sendNotification({
                userId: agentUserId,
                type: 'new_message',
                title: 'New Live Chat Message',
                message: `${visitorName.trim()} sent a live chat message: "${messageText.substring(0, 50)}..."`
              });
              
              console.log(`📧 Email notification sent to agent ${agentUserId}`);
              
            } catch (agentError) {
              console.error(`❌ Error sending notification to agent ${agentUserId}:`, agentError);
            }
          }
        }
        
        console.log('🎉 All live chat agent notifications completed');
      } catch (notificationError) {
        console.error('❌ Failed to send live chat agent notifications:', notificationError);
      }
      setError(null);
    } catch (error: any) {
      setError('Failed to send message');
      toast.error('Failed to send message');
      setNewMessage(messageText);
    } finally {
      setIsLoading(false);
    }
  };

  const closeChat = () => {
    if (session?.id) {
      pb.collection('liveChatSessions').update(session.id, {
        status: 'closed',
        closedAt: new Date().toISOString()
      }).catch(console.error);
    }

    setIsOpen(false);
    setIsMinimized(false);
    setStep('form');
    setSession(null);
    setMessages([]);
    setVisitorName('');
    setVisitorEmail('');
    setSubject('');
    setNewMessage('');
    setError(null);
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const toggleHidden = () => {
    setIsHidden(!isHidden);
    if (isOpen) {
      setIsOpen(false);
    }
  };

  const getPositionClasses = () => {
    const isHomepage = location.pathname === '/';
    
    if (isHidden) {
      return isHomepage ? 'bottom-32 sm:bottom-32 -right-8' : 'bottom-32 sm:bottom-32 -right-8';
    }
    // Position lower, closer to bottom navigation bar
    return isHomepage ? 'bottom-32 sm:bottom-32 right-2' : 'bottom-32 sm:bottom-32 right-2';
  };

  const getStatusText = (status: string) => {
    // Check if there are any non-system messages from agents
    const hasAgentResponse = messages.some(msg => 
      msg.senderType === 'agent' || (msg.senderType !== 'visitor' && msg.senderType !== 'system')
    );
    
    switch (status) {
      case 'waiting':
        return 'Waiting for agent...';
      case 'assigned':
        return hasAgentResponse ? 'Connected' : 'Agent assigned - waiting for acceptance';
      case 'active':
        return 'Connected';
      case 'closed':
        return 'Chat ended';
      default:
        return 'Unknown status';
    }
  };

  const getStatusColor = (status: string) => {
    // Check if there are any non-system messages from agents
    const hasAgentResponse = messages.some(msg => 
      msg.senderType === 'agent' || (msg.senderType !== 'visitor' && msg.senderType !== 'system')
    );
    
    switch (status) {
      case 'waiting':
        return 'bg-yellow-100 text-yellow-800';
      case 'assigned':
        return hasAgentResponse ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      {/* Chat Widget Button */}
      {!isOpen && (
        <div className={`fixed ${getPositionClasses()} z-40 transition-all duration-300 ease-in-out`}>
          <div className="relative group">
            <Button
              onClick={isHidden ? () => setIsHidden(false) : () => setIsOpen(true)}
              className="rounded-full w-10 h-10 bg-[#25D366] hover:bg-[#128C7E] text-white shadow-lg"
              size="icon"
            >
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
            </Button>
            {/* Hide button - always visible */}
            {!isHidden && (
              <div className="absolute -top-1 -right-1">
                <Button
                  variant="ghost"
                  onClick={toggleHidden}
                  className="h-4 w-4 rounded-full bg-white/95 text-red-600 hover:bg-white hover:text-red-700 shadow-md p-0"
                  size="icon"
                  title="Hide chat"
                >
                  <X className="h-2.5 w-2.5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Chat Widget */}
      {isOpen && (
        <div className={`fixed bottom-36 sm:bottom-36 right-2 z-40 w-64 max-w-[calc(100vw-1rem)]`}>
          <Card className={`shadow-xl flex flex-col overflow-hidden ${isMinimized ? 'h-auto' : 'h-auto max-h-[calc(100vh-10rem)] sm:max-h-[calc(100vh-8rem)]'}`}>
            <CardHeader className="pb-3 flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageCircle className="h-5 w-5" />
                  Live Chat
                </CardTitle>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleMinimize}
                    className="h-6 w-6 hover:bg-gray-100"
                  >
                    {isMinimized ? <Maximize className="h-4 w-4" /> : <Minimize className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={closeChat}
                    className="h-6 w-6 hover:bg-gray-100"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {session && (
                <Badge className={getStatusColor(session.status)}>
                  {getStatusText(session.status)}
                </Badge>
              )}
              {error && (
                <div className="text-xs text-red-600 bg-red-50 p-2 rounded">
                  {error}
                </div>
              )}
            </CardHeader>
            
            {!isMinimized && (
              <CardContent className="p-3 flex-1 flex flex-col min-h-0 overflow-y-auto">
                {step === 'form' ? (
                  <div className="space-y-1.5 overflow-y-auto">
                    {/* WhatsApp Quick Contact Section */}
                    <div className="bg-green-50 border border-green-200 rounded p-1.5">
                      <div className="flex items-center gap-1.5">
                        <svg className="h-3.5 w-3.5 text-green-600 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        <p className="text-[9px] text-green-800 leading-tight flex-1">Fast? WhatsApp / للتواصل السريع</p>
                        {supportContacts.length > 0 ? (
                          supportContacts.slice(0, 1).map((contact) => (
                            <Button
                              key={contact.id}
                              onClick={() => {
                                let cleanNumber = contact.phone_number.replace(/[^0-9+]/g, '');
                                if (cleanNumber.startsWith('00')) {
                                  cleanNumber = cleanNumber.substring(2);
                                }
                                cleanNumber = cleanNumber.replace('+', '');
                                // Try whatsapp:// protocol first (opens regular WhatsApp), fallback to wa.me
                                const whatsappUrl = `whatsapp://send?phone=${cleanNumber}`;
                                const webUrl = `https://api.whatsapp.com/send?phone=${cleanNumber}`;

                                // Create a hidden link to try the native protocol
                                const link = document.createElement('a');
                                link.href = whatsappUrl;
                                link.click();

                                // Fallback after a short delay if native doesn't work
                                setTimeout(() => {
                                  window.open(webUrl, '_blank');
                                }, 500);
                              }}
                              className="inline-flex items-center gap-0.5 bg-green-600 hover:bg-green-700 text-white px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors h-auto shrink-0"
                            >
                              <svg className="h-2 w-2" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                              </svg>
                              WhatsApp
                            </Button>
                          ))
                        ) : (
                          <div
                            className={`h-4 w-14 bg-green-200/50 rounded shrink-0 ${
                              isLoadingSupportContacts ? 'animate-pulse' : 'opacity-50'
                            }`}
                          />
                        )}
                      </div>
                    </div>
                    
                    {/* Or Separator */}
                    <div className="flex items-center gap-2 py-1">
                      <div className="flex-1 h-px bg-gray-200"></div>
                      <span className="text-[10px] text-gray-500 font-medium">Or</span>
                      <div className="flex-1 h-px bg-gray-200"></div>
                    </div>
                    
                    <p className="text-[9px] text-gray-500 text-center -mt-0.5">Start chat & wait for live agent</p>
                    
                    <div>
                      <label className="text-xs font-medium">Name *</label>
                      <Input
                        value={visitorName}
                        onChange={(e) => setVisitorName(e.target.value)}
                        placeholder="Your name"
                        className="mt-0.5 h-8 text-xs"
                        disabled={isLoading}
                        onKeyPress={handleKeyPressOnForm}
                      />
                    </div>
                    
                    <div>
                      <label className="text-xs font-medium">Email (optional)</label>
                      <Input
                        type="email"
                        value={visitorEmail}
                        onChange={(e) => setVisitorEmail(e.target.value)}
                        placeholder="your@email.com"
                        className="mt-0.5 h-8 text-xs"
                        disabled={isLoading}
                        onKeyPress={handleKeyPressOnForm}
                      />
                    </div>
                    
                    <div>
                      <label className="text-xs font-medium">Subject (optional)</label>
                      <Input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="What can we help you with?"
                        className="mt-0.5 h-8 text-xs"
                        disabled={isLoading}
                        onKeyPress={handleKeyPressOnForm}
                      />
                    </div>
                    
                    <Button
                      onClick={startChat}
                      disabled={!visitorName.trim() || isLoading}
                      className="w-full mt-2 h-8 text-xs"
                    >
                      {isLoading ? 'Starting Chat...' : 'Start Chat'}
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col h-full">
                    {/* Messages Area */}
                    <div className="flex-1 min-h-0 mb-3">
                      <ScrollArea className="h-full">
                        <div className="space-y-2 pr-2">
                          {messages.map((message) => (
                            <div
                              key={message.id}
                              className={`flex ${
                                message.senderType === 'visitor' ? 'justify-end' : 'justify-start'
                              }`}
                            >
                              <div
                                className={`max-w-[80%] p-2 rounded-lg text-sm ${
                                  message.senderType === 'visitor'
                                    ? 'bg-blue-600 text-white'
                                    : message.senderType === 'system'
                                    ? 'bg-gray-200 text-gray-700 italic'
                                    : 'bg-gray-100 text-gray-900'
                                }`}
                              >
                                {message.senderType !== 'visitor' && message.senderType !== 'system' && (
                                  <div className="font-medium text-xs mb-1">
                                    {message.senderName}
                                  </div>
                                )}
                                <div>{message.message}</div>
                                <div className="text-xs mt-1 opacity-70">
                                  {new Date(message.created).toLocaleTimeString()}
                                </div>
                              </div>
                            </div>
                          ))}
                          <div ref={messagesEndRef} />
                        </div>
                      </ScrollArea>
                    </div>

                    {/* Message Input Area */}
                    <div className="flex-shrink-0">
                      {session && session.status !== 'closed' && (
                        <div className="flex gap-2">
                          <Input
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type a message..."
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                              }
                            }}
                            className="flex-1 text-sm"
                            disabled={isLoading}
                          />
                          <Button
                            onClick={sendMessage}
                            disabled={!newMessage.trim() || isLoading}
                            size="icon"
                            className="h-8 w-8 flex-shrink-0"
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      {(!session || session.status === 'closed') && (
                        <div className="text-center text-sm text-gray-500 py-2">
                          {session?.status === 'closed' ? 'This chat has ended.' : 'Starting chat...'}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        </div>
      )}
    </>
  );
};

export default LiveChatWidget;
